import React, { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import type { ControlEvent, DepartmentOverview } from '../../types/aai';
import {
  Action, Card, Chip, Divided, EmptyState, ErrorNote, Kicker, ListRow, Loading, NotMeasured, PageBody, PageHead, Panel,
  PanelHead, RouteMark, Stat,
} from '../../components/aa';
import { Tiles } from '../owner/ControlDrawers';
import { EvidenceRulesDrawer } from '../owner/AdoptionPage';
import { relativeTime } from '../partner/format';
import { ActionUpdateDrawer } from '../champion/ActionUpdateDrawer';
import { ACTION_KIND_LABEL, isAwaiting, isClosed, payloadText, shortName, sideOf, statusOf } from '../champion/actionStatus';
import { SupportRequestDrawer } from './SupportRequestDrawer';
import { departmentName, plural, useDepartment } from './useDepartment';

/*
 * Team AI readiness, for the Department Head. Protected aggregates, the shared
 * blocker, and one next support action. No individual prompts, answers or
 * rankings: groups under the reporting threshold read as protected.
 */

const count = (value: number | null | undefined, suppressed: boolean): React.ReactNode => {
  if (suppressed || value === null || value === undefined) return <NotMeasured reason="Protected" />;
  return `${value}`;
};

interface TeamPath {
  key: string;
  title: string;
  kind: 'Quick Win' | 'Delivery Project';
  linked: string;
}

const teamPaths = (data: DepartmentOverview): TeamPath[] => {
  const wins = data.quick_wins.filter((w) => w.scope === 'team').map((w) => ({
    key: `qw-${w.id}`, title: `${w.name} readiness`, kind: 'Quick Win' as const, linked: w.name,
  }));
  const projects = data.work.filter((r) => !r.tool_skill_id).map((r) => ({
    key: `dp-${r.id}`, title: `${r.title} readiness`, kind: 'Delivery Project' as const, linked: r.title,
  }));
  return [...wins, ...projects];
};

/** The open action that concerns one team path, matched by the path it was recorded on. */
const actionForPath = (actions: ControlEvent[], path: string): ControlEvent | null => actions
  .find((e) => !isClosed(e) && payloadText(e, 'path') === path) || null;

const actionChip = (e: ControlEvent, me: string): React.ReactNode => {
  const state = statusOf(e.status);
  let who = `by ${shortName(e.actor_email)}`;
  if (sideOf(e, me) === 'raiser') who = 'by you';
  return <Chip tone={state.tone} dot>{state.label} · {who} · {relativeTime(e.created_at)}</Chip>;
};

const DepartmentAdoptionPage: React.FC = () => {
  const overview = useDepartment();
  const [support, setSupport] = useState<{ path: string; linked: string | null } | null>(null);
  const [openAction, setOpenAction] = useState<ControlEvent | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const me = localStorage.getItem('user_email') || '';

  if (overview.loading) return <PageBody><Loading label="Loading team readiness" /></PageBody>;
  if (overview.error || !overview.data) return <PageBody><ErrorNote message={overview.error || 'Something went wrong.'} /></PageBody>;

  const data = overview.data;
  const name = departmentName(data);
  const r = data.readiness;
  const suppressed = !r || r.suppressed;
  const needSupport = r?.need_support ?? null;
  const paths = teamPaths(data);
  const actions = data.actions || [];
  const latestSupport = data.support_requests[0] || null;
  const latestOpen = actions.find((e) => !isClosed(e)) || null;
  const mine = actions.filter((e) => !isClosed(e) && sideOf(e, me) === 'raiser');
  const closed = actions.filter(isClosed);
  const askedOfMe = latestOpen !== null && isAwaiting(latestOpen) && sideOf(latestOpen, me) === 'recipient';
  const firstLinked = data.quick_wins.find((w) => w.scope === 'team')?.name || paths[0]?.linked || null;

  let supportStatus: React.ReactNode = <Chip>Nothing waiting</Chip>;
  if (suppressed) supportStatus = <Chip>Protected</Chip>;
  if (!suppressed && needSupport !== null && needSupport > 0) supportStatus = <Chip tone="run">Action needed</Chip>;
  if (latestOpen) supportStatus = actionChip(latestOpen, me);

  let peopleValue: React.ReactNode = <NotMeasured reason="No department recorded" />;
  if (r) peopleValue = `${r.people}`;
  let supportTone: 'attention' | 'default' = 'default';
  if (!suppressed && needSupport) supportTone = 'attention';
  let sizeWord = 'size protected';
  if (r) sizeWord = `${r.people} people`;
  let blockerFoot: string | undefined;
  if (!suppressed && needSupport) blockerFoot = `${plural(needSupport, 'person shares', 'people share')} a first-use blocker`;
  let affectedPeople: number | null = null;
  if (!suppressed) affectedPeople = needSupport;
  const markKind = (kind: TeamPath['kind']): 'quick_win' | 'project' => {
    if (kind === 'Quick Win') return 'quick_win';
    return 'project';
  };

  const pathStatus = (path: string): React.ReactNode => {
    const action = actionForPath(actions, path);
    if (action) return actionChip(action, me);
    if (!suppressed && needSupport !== null && needSupport > 0) return <Chip tone="warn">Needs support</Chip>;
    return <Chip tone="run">In progress</Chip>;
  };

  return (
    <PageBody>
      <PageHead
        align="center"
        eyebrow={name}
        title="Team AI readiness"
        sub="Prepare people for approved Projects and support first use without viewing personal responses."
      />

      <div className="mb-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Stat label="People in scope" value={peopleValue} evidence={name} />
        <Stat label="Ready" value={count(r?.ready, suppressed)} evidence="Protected aggregate" />
        <Stat label="Learning" value={count(r?.learning, suppressed)} evidence="Protected aggregate" />
        <Stat tone={supportTone} label="Need support" value={count(needSupport, suppressed)} evidence="Shared blocker" />
      </div>

      <div className="mb-5 grid gap-5 lg:grid-cols-[2.6fr_1fr]">
        <Panel>
          <div className="px-5 pb-6 pt-5 md:px-6 md:pt-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Kicker className="mb-2">Needs support</Kicker>
                <h2 className="m-0 text-[24px] font-bold leading-[30px] tracking-[-0.005em] text-ink">
                  {suppressed && 'Readiness is protected for this team'}
                  {!suppressed && needSupport === 0 && 'Nobody is at the first stage'}
                  {!suppressed && needSupport !== null && needSupport > 0 && 'First-use support before the next Project'}
                </h2>
                <p className="m-0 mt-1.5 text-[13px] leading-[21.5px] text-ink-dim">
                  {suppressed && (r?.suppressed_reason || `Groups under ${data.min_group_size} people are not reportable.`)}
                  {!suppressed && needSupport !== null && `${plural(needSupport, 'person', 'people')} · ${name} · stage measured from the team's own baselines`}
                </p>
              </div>
              {supportStatus}
            </div>
            <div className="mt-5">
              <Tiles
                cols={2}
                facts={[
                  { label: 'Linked Project', value: firstLinked || 'No linked Project yet' },
                  { label: 'Owner', value: name },
                  { label: 'Support format', value: 'Group session, arranged by the program manager' },
                  { label: 'Evidence', value: 'Readiness stage per team, never per person' },
                ]}
              />
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              {askedOfMe && latestOpen && (
                <Action arrow onClick={() => setOpenAction(latestOpen)}>Respond to {shortName(latestOpen.actor_email)}</Action>
              )}
              <Action
                variant={askedOfMe ? 'quiet' : undefined}
                disabled={!r}
                reason="No department recorded"
                onClick={() => setSupport({ path: 'first_use', linked: firstLinked })}
              >
                Schedule support
              </Action>
              <Action variant="quiet" onClick={() => setRulesOpen(true)}>How adoption evidence works</Action>
            </div>
          </div>
        </Panel>

        <Card pad="lg">
          <div className="flex items-start gap-3">
            <ShieldCheck size={22} strokeWidth={1.75} className="mt-0.5 shrink-0 text-ink" />
            <div>
              <Kicker className="mb-2">Your view</Kicker>
              <div className="text-[18px] font-bold leading-[24px] text-ink">Department Head</div>
              <p className="m-0 mt-2 text-[12.5px] leading-[19px] text-ink-muted">
                No individual prompts, answers, or rankings. Groups below {data.min_group_size} people are not reportable.
              </p>
              <p className="m-0 mt-3 text-[12.5px] leading-[19px] text-ink-muted">
                Support requests are recorded on the team path and sent to the program manager. Nobody is named in the request.
              </p>
            </div>
          </div>
        </Card>
      </div>

      {mine.length > 0 && (
        <Panel className="mb-5">
          <PanelHead
            kicker="Your open requests"
            title={`${plural(mine.length, 'request', 'requests')} you raised and nobody has closed`}
            desc="A request you made to the program stays open until it is delivered or you withdraw it. Mark it delivered once the session happened, or cancel it if it is no longer needed."
          />
          <Divided>
            {mine.map((e) => {
              const state = statusOf(e.status);
              let responded = 'No answer yet';
              const respondedBy = (e.payload || {}).responded_by;
              if (typeof respondedBy === 'string' && respondedBy) responded = `${state.label} by ${shortName(respondedBy)}`;
              return (
                <ListRow
                  key={e.id}
                  mark={<RouteMark kind="adoption" size="lg" />}
                  title={e.subject}
                  badge={<Chip tone={state.tone} dot>{ACTION_KIND_LABEL[e.kind] || e.kind} · {state.label}</Chip>}
                  desc={payloadText(e, 'note') || undefined}
                  meta={[
                    { label: 'Raised', value: relativeTime(e.created_at) },
                    { label: 'Response', value: responded },
                  ]}
                  right={<Action size="sm" variant="quiet" arrow onClick={() => setOpenAction(e)}>Update</Action>}
                />
              );
            })}
          </Divided>
        </Panel>
      )}

      <Panel>
        <PanelHead kicker="Current work" title="Active team paths" action={<span className="text-[12px] text-ink-soft">{plural(paths.length, 'item', 'items')}</span>} />
        {paths.length === 0 && (
          <EmptyState title="No team paths yet" reason="A team path appears for every Quick Win published to your department and every Delivery Project in flight." />
        )}
        <Divided>
          {paths.map((p) => (
            <ListRow
              key={p.key}
              mark={<RouteMark kind={markKind(p.kind)} size="lg" />}
              title={p.title}
              desc={`${name} · ${sizeWord} · ${p.kind}`}
              foot={blockerFoot}
              right={(
                <>
                  {pathStatus(p.key)}
                  {actionForPath(actions, p.key) && (
                    <Action size="sm" variant="quiet" onClick={() => setOpenAction(actionForPath(actions, p.key))}>Open</Action>
                  )}
                  {!actionForPath(actions, p.key) && (
                    <Action size="sm" variant="quiet" onClick={() => setSupport({ path: p.key, linked: p.linked })}>
                      Schedule support
                    </Action>
                  )}
                </>
              )}
            />
          ))}
        </Divided>
      </Panel>

      <Panel className="mt-5">
        <PanelHead
          kicker="History"
          title="Closed actions for your team"
          desc="Support and demonstrations that were delivered, held, declined or cancelled, whoever raised them. Nothing moves on a closed action; open one to read how it ended."
          action={<span className="text-[12px] text-ink-soft">{plural(closed.length, 'closed', 'closed')}</span>}
        />
        {closed.length === 0 && <EmptyState title="Nothing closed yet" reason="Closed actions stay here as the record of what was asked for your team and how it ended." />}
        <Divided>
          {closed.slice(0, 20).map((e) => {
            const state = statusOf(e.status);
            let by = 'not recorded';
            const respondedBy = (e.payload || {}).responded_by;
            if (typeof respondedBy === 'string' && respondedBy) by = shortName(respondedBy);
            let mark: 'adoption' | 'quick_win' = 'adoption';
            if (e.kind === 'demonstration') mark = 'quick_win';
            let closedAt = e.created_at;
            const closedValue = (e.payload || {}).closed_at || (e.payload || {}).responded_at;
            if (typeof closedValue === 'string') closedAt = closedValue;
            return (
              <ListRow
                key={e.id}
                mark={<RouteMark kind={mark} size="lg" />}
                title={e.subject}
                badge={<Chip tone={state.tone} dot>{ACTION_KIND_LABEL[e.kind] || e.kind} · {state.label}</Chip>}
                meta={[
                  { label: 'Raised by', value: shortName(e.actor_email), foot: relativeTime(e.created_at) },
                  { label: 'Closed', value: `${state.label} by ${by}`, foot: relativeTime(closedAt) },
                ]}
                right={<Action size="sm" variant="quiet" onClick={() => setOpenAction(e)}>Open</Action>}
              />
            );
          })}
        </Divided>
      </Panel>

      {support && (
        <SupportRequestDrawer
          department={name}
          affectedWork={support.linked}
          people={affectedPeople}
          minGroupSize={data.min_group_size}
          path={support.path}
          latest={latestSupport}
          onClose={() => setSupport(null)}
          onRecorded={overview.reload}
        />
      )}
      {rulesOpen && <EvidenceRulesDrawer managers={[]} managerLabel="Program manager" onClose={() => setRulesOpen(false)} />}
      {openAction && <ActionUpdateDrawer event={openAction} onUpdated={() => { setOpenAction(null); overview.reload(); }} onClose={() => setOpenAction(null)} />}
    </PageBody>
  );
};

export default DepartmentAdoptionPage;
